import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { ComparatorResult, EMPTY_ARRAY, EMPTY_STRING, formatNumber } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import type {
  ConditionOperator,
  CreateFieldInstanceValueInputs,
  FieldFields,
  FieldInstanceFields,
  FieldInstanceFieldsWithBaseValues,
  FieldInstanceValueInputType,
  FieldUnit,
  PropertyDataType,
  PropertyTypeFields,
  SpectrumFieldVersionFields,
  TableFieldInstanceFields,
  UpdateFieldInstanceValueInputs,
  UpdateStartingConditionsInputs,
  ViewFilterFields,
} from "@regrello/graphql-api";
import { RegrelloIcon } from "@regrello/ui-core";
import type { FieldPath, FieldValues } from "react-hook-form";

import { ValidationRules } from "../../../../../constants/globalConstants";
import type { FieldInstanceBaseFields } from "../../../../../types";
import { numberComparator } from "../../../../../utils/comparators/numberComparator";
import { getFieldInstanceId } from "../../../../../utils/customFields/getFieldInstanceId";
import { getRegrelloDefaultFilterDefinitionCurrencyValue } from "../../../../../utils/filters/v2/filterKinds/core/filterCurrency";
import { getRegrelloFilterDefinitionNumberValue } from "../../../../../utils/filters/v2/filterKinds/core/filterNumber";
import { mergeReactHookFormValidators } from "../../../../../utils/formFields/mergeReactHookFormValidators";
import { getErrorMessageWithPayload } from "../../../../../utils/getErrorMessageWithPayload";
import { TableCellDefaultWidths } from "../../../../../utils/tableCellWidthUtils";
import { RegrelloControlledFormFieldNumber } from "../../formFields/controlled/RegrelloControlledFormFieldNumber";
import {
  getDecimalPlacesFromFieldVersion,
  getDisallowGroupingsFloatFromFieldVersion,
} from "../../spectrumFields/utils/spectrumFieldConstraintUtils";
import { RegrelloCustomFieldMultiValuePopover } from "../components/RegrelloCustomFieldMultiValuePopover";
import { CustomFieldPluginRegistrar } from "./registry/customFieldPluginRegistrar";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  GetCreateFieldInstanceValueInputsFromFormValueParams,
} from "./types/CustomFieldPlugin";
import { createViewColumnsFromField } from "./utils/createViewColumnsFromField";
import { DEFAULT_INPUT_TYPE_IF_NO_VALUE, getConditionOperatorsByType } from "./utils/customFieldConstants";
import { extractAtMostOneTabularArrayOrThrow } from "./utils/extractAtMostOneTabularArrayOrThrow";
import { extractAtMostOneValueOrThrow } from "./utils/extractAtMostOneValueOrThrow";
import {
  getIsFieldInstanceFields,
  getIsFieldInstanceValueWithCrossWorkflowFields,
} from "./utils/fieldInstanceTypeguards";
import { getUpdateStartingConditionsInputsForEmptyOperators } from "./utils/getUpdateStartingConditionsInputsForEmptyOperators";
import { validateRequired } from "./utils/validateBulkUploadCellValueUtils";

// TODO Misc: Turn the following into warnings and handle gracefully so the app doesn't crash in
// case of field misconfiguration.
const ERROR_INVALID_FIELD = "Provided 'currency' field is invalid";
const WARNING_INVALID_FORM_VALUE = "Provided 'currency'-field form value is not a number string";
const ERROR_INVALID_VALUE_COUNT = "Provided 'currency' field instance cannot have multiple values";
const ERROR_INVALID_VALUE_TYPE = "Provided 'currency' field instance value must have type 'FieldInstanceValueFloat'";
const WARNING_UNEXPECTED_DEFINED_FORM_VALUE =
  "Provided 'currency' field form value must not be defined given the provided operator type";
const WARNING_INVALID_OPERATOR_TYPE = "Provided operator type is invalid for 'currency' fields";

/**
 * Currency fields should show 2 decimal places by default.
 */
const DEFAULT_DECIMAL_PLACES = 2;

function canProcessPropertyDataType(propertyDataType: PropertyDataType): boolean {
  return propertyDataType === "FLOAT";
}

function canProcessField(field: FieldFields): boolean {
  return (
    !field.isMultiValued &&
    field.allowedValues.length === 0 &&
    canProcessPropertyDataType(field.propertyType.dataType) &&
    field.fieldUnit != null
  );
}

type CurrencyFieldPluginType = CustomFieldPlugin<number | null>;

const getConditionOperators: CurrencyFieldPluginType["getConditionOperators"] = () =>
  getConditionOperatorsByType("CURRENCY");

const renderDisplayValueFromStringValue: CurrencyFieldPluginType["renderDisplayValueFromStringValue"] = (
  fieldVersion,
  value,
) => {
  return renderDisplayValueInternal(fieldVersion, Number(value));
};

const renderDisplayValue: CurrencyFieldPluginType["renderDisplayValue"] = (fieldInstance) => {
  const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
  const value = frontendFieldInstance.value ?? null;

  return renderDisplayValueInternal(fieldInstance.spectrumFieldVersion, value);
};

function renderDisplayValueInternal(fieldVersion: SpectrumFieldVersionFields, value: number | null) {
  const fieldUnit = fieldVersion.fieldUnit;

  if (fieldUnit == null) {
    console.warn("Currency field unexpectedly has no field unit. This is invalid. Returning the value as is.");
    return value;
  }

  if (!isFiniteNumber(value)) {
    return "";
  }

  const formattedNumber = formatNumber(value, i18n.locale, {
    decimalPlaces: getDecimalPlacesFromFieldVersion(fieldVersion) ?? DEFAULT_DECIMAL_PLACES,
    disallowGroupings: getDisallowGroupingsFloatFromFieldVersion(fieldVersion) ?? false,
  });

  return getFieldUnitDisplayValue(fieldUnit, formattedNumber);
}

const sortComparator: CurrencyFieldPluginType["sortComparator"] = (
  fieldInstance1,
  fieldInstance2,
  direction = "asc",
): ComparatorResult => {
  if (direction === "desc") {
    return CurrencyFieldPlugin.sortComparator(fieldInstance2, fieldInstance1, "asc");
  }

  if (fieldInstance1 == null) {
    return ComparatorResult.BEFORE;
  }

  if (fieldInstance2 == null) {
    return ComparatorResult.AFTER;
  }

  const fieldPlugin = CustomFieldPluginRegistrar.getPluginForField(fieldInstance1.field);

  const value1 = Number(fieldPlugin.getValueForFrontend(fieldInstance1));
  const value2 = Number(fieldPlugin.getValueForFrontend(fieldInstance2));

  return numberComparator(value1, value2);
};

/**
 * Describes a custom field that holds a currency value consisting of a number and a symbol.
 */
export const CurrencyFieldPlugin: CurrencyFieldPluginType = {
  uri: "com.regrello.customField.currency",
  version: "1.0.0",

  canProcessField: (field: FieldFields): boolean => {
    return canProcessField(field);
  },

  canProcessFieldInstance: (fieldInstance: FieldInstanceBaseFields): boolean => {
    try {
      translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
      return true;
    } catch (_error) {
      return false;
    }
  },

  canProcessPropertyDataType,

  findPropertyTypeIdFromLoadedPropertyTypeIds: (propertyTypes: PropertyTypeFields[]): number | undefined => {
    return propertyTypes.find((propertyType) => propertyType.dataType === "FLOAT")?.id;
  },

  getColumnsForTable: createViewColumnsFromField,

  getConditionOperators,

  getCreateFieldInstanceValueInputsFromFormValue: (
    inputs: GetCreateFieldInstanceValueInputsFromFormValueParams,
  ): CreateFieldInstanceValueInputs => {
    const { displayOrder, field, inputType, displayContext, sourceFieldInstanceId, spectrumFieldVersion, value } =
      inputs;
    if (value != null && !isFiniteNumber(value)) {
      throw new Error(getErrorMessageWithPayload(WARNING_INVALID_FORM_VALUE, { field, inputType, value }));
    }
    return {
      fieldId: field.id,
      floatValue: isFiniteNumber(value) ? value : undefined,
      inputType,
      displayContext: displayContext ?? "DEFAULT",
      displayOrder,
      spectrumFieldVersionId: spectrumFieldVersion.id,
      sourceFieldInstanceValueId: sourceFieldInstanceId,
    };
  },

  getCrossWorkflowSinksFieldInstanceIds: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number[] => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSinksFieldInstanceIds;
  },

  getCrossWorkflowSourceFieldInstanceIdFromValue: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSourceFieldInstanceId;
  },

  getFieldDisplayName: (): string => {
    return t`Currency`;
  },

  getFilterDefinition: (_field: FieldFields) => {
    return getRegrelloDefaultFilterDefinitionCurrencyValue();
  },

  getFilterDefinitionWithValues: (_field: FieldFields, filter: ViewFilterFields) => {
    if (filter.value == null) {
      return undefined;
    }

    const value = Number.parseFloat(filter.value);
    return getRegrelloFilterDefinitionNumberValue(filter.operator, value);
  },

  getIconName: () => {
    return "currency-field";
  },

  getNameTemplateDisplayValueFromFormValue: (value, fieldInstance, _options) => {
    if (value == null || !isFiniteNumber(value)) {
      return undefined;
    }
    return renderDisplayValueInternal(fieldInstance.spectrumFieldVersion, value);
  },

  getEmptyValueForFrontend: (_, defaultValue) => {
    if (defaultValue == null || defaultValue === EMPTY_STRING) {
      return null;
    }
    const defaultValueAsFloat = Number.parseFloat(defaultValue);
    if (!isFiniteNumber(defaultValueAsFloat)) {
      return null;
    }
    return defaultValueAsFloat;
  },

  getEmptyTabularValueForFrontend: (rowNumber: number): { value: string | null; rowNumber: number } => {
    return { value: null, rowNumber };
  },

  getPreferredHomeTableColumnWidth: () => {
    return TableCellDefaultWidths.NUMBER_CELL;
  },

  getSourceFieldInstance: (_fieldInstance: FieldInstanceFields): FieldInstanceFields | undefined => {
    return undefined;
  },

  getSourceFieldInstanceId: (fieldInstance: FieldInstanceBaseFields): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sourceFieldInstanceId;
  },

  getSourceFieldInstanceInputType: (
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
  ): FieldInstanceValueInputType | undefined => {
    const sourceValue = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return sourceValue.sourceFieldInstanceInputType;
  },

  getSinkFieldInstanceIds: (fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sinksFieldInstanceIds;
  },

  getUpdateStartingConditionsInputsFromFormValues: (
    leftFieldInstance: FieldInstanceFields,
    value: unknown,
    operator: ConditionOperator,
  ): UpdateStartingConditionsInputs | undefined => {
    if (!isOperatorValid(operator)) {
      console.warn(WARNING_INVALID_OPERATOR_TYPE, {
        leftFieldInstance,
        operator,
      });
      return undefined;
    }

    if (operator === "EMPTY" || operator === "NOT_EMPTY") {
      if (value != null) {
        console.warn(WARNING_UNEXPECTED_DEFINED_FORM_VALUE, {
          leftFieldInstance,
          value,
        });
      }
      return getUpdateStartingConditionsInputsForEmptyOperators(getFieldInstanceId(leftFieldInstance), operator);
    }

    if (operator === "BETWEEN") {
      // (zstanik): For the `BETWEEN` operator, the value is expected to be an array of 2
      // stringified numbers: a lower bound and upper bound.
      if (!Array.isArray(value) || value.length !== 2 || value.some((str) => Number.isNaN(Number.parseFloat(str)))) {
        console.warn(WARNING_INVALID_FORM_VALUE, { leftFieldInstance, value });
        return undefined;
      }

      return {
        leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
        operatorV2: operator,
        rightFloatMultiValue: value.map((str) => Number.parseFloat(str)),
        rightIntMultiValue: EMPTY_ARRAY,
        rightPartyIDMultiValue: EMPTY_ARRAY,
        rightStringMultiValue: EMPTY_ARRAY,
        rightTimeMultiValue: EMPTY_ARRAY,
        rightDateMultiValue: EMPTY_ARRAY,
      };
    }

    // (zstanik): For all other operators, the value is expected to be a finite number.
    if (!isFiniteNumber(value)) {
      console.warn(WARNING_INVALID_FORM_VALUE, { leftFieldInstance, value });
      return undefined;
    }

    return {
      leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
      operatorV2: operator,
      rightFloatValue: value,
      rightFloatMultiValue: EMPTY_ARRAY,
      rightIntMultiValue: EMPTY_ARRAY,
      rightPartyIDMultiValue: EMPTY_ARRAY,
      rightStringMultiValue: EMPTY_ARRAY,
      rightTimeMultiValue: EMPTY_ARRAY,
      rightDateMultiValue: EMPTY_ARRAY,
    };
  },

  // (hchen): Currently this is only used for handling the edge case when flipping the field
  // instance input type from OPTIONAL to REQUESTED (i.e. required) when the a previously OPTIONAL
  // field is set as a native field on a future task. It's not necessary to implement for field
  // types other than date and user, since we only support those as native field now.
  getUpdateFieldInstanceValueInputsFromFieldInstance: (
    _fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): UpdateFieldInstanceValueInputs[] => {
    throw new Error("not implmented yet");
  },

  getValueForFrontend: (fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) => {
    return extractAtMostOneValueOrThrow({
      fieldInstance,
      fieldInstanceValueTypeName: "FieldInstanceValueFloat",
      errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
      errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
      getterIfNoValue: () => null,
      getterIfValue: (fieldInstanceValue) => {
        const value = fieldInstanceValue.floatValue;
        if (value == null || !isFiniteNumber(value)) {
          return null;
        }
        return value;
      },
    });
  },

  getTabularValueForFrontend: (fieldInstance: TableFieldInstanceFields) => {
    return translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);
  },

  hasFieldUnit: (): boolean => {
    return true;
  },

  isCreateAndEditAllowed: true,

  isFeatureFlagEnabled: (): boolean => {
    return true;
  },

  isFieldInstanceEmpty: (fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.value == null;
  },

  isFieldInstanceValueEqual: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    fieldInstance2: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    const frontendFieldInstance2 = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance2);
    return frontendFieldInstance.value === frontendFieldInstance2.value;
  },

  isFieldInstanceValueUnchanged: (fieldInstance, proposedChange): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    if (frontendFieldInstance.sourceFieldInstanceId !== proposedChange.sourceFieldInstanceValueId) {
      return false;
    }

    if (frontendFieldInstance.inputType !== proposedChange.inputType) {
      return false;
    }

    return (
      (frontendFieldInstance.value == null && proposedChange.floatValue == null) ||
      frontendFieldInstance.value === proposedChange.floatValue
    );
  },

  isFieldInstanceTabularValueUnchanged: (fieldInstance, proposedChange) => {
    const rows = translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);
    const originalRow = rows.find((row) => row.rowNumber === proposedChange.rowNumber);
    if (originalRow == null) {
      return false; // This potentially is a new row
    }

    return originalRow.value === proposedChange.floatValue;
  },

  isNeedsFieldUnit: (): boolean => {
    return true;
  },

  renderDisplayValue: renderDisplayValue,

  renderDisplayValueFromStringValue: renderDisplayValueFromStringValue,

  renderFormField: <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    if (!canProcessField(field)) {
      throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
    }
    const { operator } = props;

    const fieldInstance = props.fieldInstance;
    const isDeleted = fieldInstance?.field.deletedAt != null;

    // If an operator is specified, then we don't want to impose min/max constraints, since the user
    // should be able to specify any value in conditions.

    const fieldVersion = props.fieldInstance?.spectrumFieldVersion;

    const decimalPlaces =
      (fieldVersion != null ? getDecimalPlacesFromFieldVersion(fieldVersion) : undefined) ?? DEFAULT_DECIMAL_PLACES;

    const disallowGroupings =
      (fieldVersion != null ? getDisallowGroupingsFloatFromFieldVersion(fieldVersion) : undefined) ?? false;

    if (operator != null && !isOperatorValid(operator)) {
      console.warn(WARNING_INVALID_OPERATOR_TYPE, {
        field,
        operator,
      });
      return undefined;
    }

    // Merge validation rules into a single function that invokes validateValue as well as other
    // validators specific to this field type.
    const controllerRules: typeof props.controllerProps.rules = {
      ...props.controllerProps.rules,
      validate: mergeReactHookFormValidators(
        props.controllerProps.rules?.validate,
        ValidationRules.NUMBER.validate.isNumber,
      ),
    };

    return (
      <RegrelloControlledFormFieldNumber
        {...props}
        key={`${props.controllerProps.name}-${props.keySuffix}`}
        controllerProps={{
          ...props.controllerProps,
          rules: controllerRules,
        }}
        currencySymbol={props.fieldInstance?.spectrumFieldVersion?.fieldUnit?.symbol ?? field.fieldUnit?.symbol}
        dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
        formatOptions={{
          // Show exactly the number of decimal places specified by the field version.
          minimumFractionDigits: decimalPlaces != null ? decimalPlaces : undefined,
          maximumFractionDigits: decimalPlaces != null ? decimalPlaces : undefined,
          useGrouping: !disallowGroupings,
        }}
        infoTooltipText={props.description}
        isDeleted={isDeleted}
      />
    );
  },

  renderIcon: (props) => {
    return <RegrelloIcon {...props} iconName="currency-field" />;
  },

  renderMultipleDisplayValuesForDataGrid: (fieldInstances, options) => {
    if (fieldInstances.length === 0) {
      return null;
    }
    if (fieldInstances.length === 1) {
      return renderDisplayValue(fieldInstances[0], { context: options?.context ?? "table" });
    }

    const instancesWithSource = fieldInstances.map((fieldInstance) => {
      return {
        content: renderDisplayValue(fieldInstance, { context: options?.context ?? "table" }),
        workflowName: fieldInstance.workflow?.name,
        stageName: fieldInstance.actionItem?.workflowReference?.stageName,
        taskName: fieldInstance.actionItem?.name,
      };
    });

    return <RegrelloCustomFieldMultiValuePopover instancesWithSource={instancesWithSource} />;
  },

  sortComparator,

  validateBulkUploadCellValue: (_field, value, options) => {
    const valueAsString = value.trim();

    if (options.isRequired) {
      const result = validateRequired(valueAsString, options.isRequired);
      if (result != null) {
        return result;
      }
    }

    const valueAsDecimalNumber = Number.parseFloat(valueAsString);

    if (valueAsString === EMPTY_STRING) {
      return {
        value: valueAsString,
      };
    }

    if (!Number.isFinite(valueAsDecimalNumber)) {
      return {
        value: valueAsString,
        info: [{ level: "error", message: t`Must be a valid decimal number` }],
      };
    }

    if (valueAsDecimalNumber < Number.MIN_SAFE_INTEGER || valueAsDecimalNumber > Number.MAX_SAFE_INTEGER) {
      return {
        value: valueAsString,
        info: [{ level: "error", message: t`Number is out of range` }],
      };
    }

    return {
      value: String(valueAsDecimalNumber),
    };
  },
};

interface FrontendCurrencyFieldInstance {
  crossWorkflowSinksFieldInstanceIds: number[];
  crossWorkflowSourceFieldInstanceId: number | undefined;
  /** The denomination in which the currency value is expressed (e.g., US dollars, Euros). */
  fieldUnit: {
    /** The currency denomination's unique ID. */
    id: number;

    /** The currency denomination's abbreviated display name (e.g., USD, EUR). */
    name: string;

    /** The currency denomination's symbol (e.g., $, £). */
    symbol: string;
  };
  inputType: FieldInstanceValueInputType;
  name: string;
  sinksFieldInstanceIds: number[];
  sourceFieldInstanceId: number | undefined;
  sourceFieldInstanceInputType: FieldInstanceValueInputType | undefined;

  value: number | null;
}

function translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(
  fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
): FrontendCurrencyFieldInstance {
  const { field, spectrumFieldVersion } = fieldInstance;

  if (!canProcessField(field)) {
    throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
  }

  const fieldUnit = spectrumFieldVersion?.fieldUnit ?? field.fieldUnit;
  const { name } = field;

  // (clewis: We already verified this in canProcessField, but best to have a redundant check here:
  if (fieldUnit == null) {
    throw new Error(ERROR_INVALID_FIELD);
  }

  const isFieldInstanceFields = getIsFieldInstanceFields(fieldInstance);

  return extractAtMostOneValueOrThrow({
    fieldInstance,
    fieldInstanceValueTypeName: "FieldInstanceValueFloat",
    errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: () => ({
      name,
      fieldUnit,
      inputType: DEFAULT_INPUT_TYPE_IF_NO_VALUE,
      crossWorkflowSinksFieldInstanceIds: EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: undefined,
      sinksFieldInstanceIds: EMPTY_ARRAY,
      sourceFieldInstanceId: undefined,
      sourceFieldInstanceInputType: undefined,
      value: null,
    }),
    getterIfValue: (fieldInstanceValue) => ({
      name,
      fieldUnit,
      inputType: fieldInstanceValue.inputType,
      crossWorkflowSinksFieldInstanceIds:
        getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValue) &&
        fieldInstanceValue.crossWorkflowSinksFieldInstanceValueFloat != null
          ? fieldInstanceValue.crossWorkflowSinksFieldInstanceValueFloat.map((value) => value.id)
          : EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValue)
        ? fieldInstanceValue.crossWorkflowSourceFieldInstanceValueFloat?.id
        : undefined,
      sinksFieldInstanceIds: isFieldInstanceFields
        ? fieldInstanceValue.sinksFieldInstanceValueFloat?.map(({ id }) => id)
        : EMPTY_ARRAY,
      sourceFieldInstanceId: isFieldInstanceFields ? fieldInstanceValue.sourceFieldInstanceValueFloat?.id : undefined,
      sourceFieldInstanceInputType: isFieldInstanceFields
        ? fieldInstanceValue.sourceFieldInstanceValueFloat?.inputType
        : undefined,
      value:
        fieldInstanceValue.floatValue == null || !Number.isFinite(fieldInstanceValue.floatValue)
          ? null
          : fieldInstanceValue.floatValue,
    }),
  });
}

function getFieldUnitDisplayValue(
  fieldUnit: Pick<FieldUnit, "symbol"> | undefined,
  value: number | string | null,
): string {
  if (value == null || value === "") {
    return "";
  }

  const symbol = fieldUnit?.symbol;
  return symbol != null ? `${symbol}${value}` : String(value);
}

function isOperatorValid(
  operator: ConditionOperator,
): operator is
  | "EMPTY"
  | "NOT_EMPTY"
  | "GREATER_THAN_OR_EQUALS"
  | "LESS_THAN_OR_EQUALS"
  | "EQUALS"
  | "NOT_EQUALS"
  | "BETWEEN" {
  return getConditionOperators().find((stageStartOperator) => stageStartOperator.operator === operator) != null;
}

/** Type guard that invokes Number.isFinite *and* narrows the type to number. */
function isFiniteNumber(value: unknown): value is number {
  return Number.isFinite(value);
}

function translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance: TableFieldInstanceFields) {
  return extractAtMostOneTabularArrayOrThrow({
    fieldInstance,
    fieldInstanceValueTypeName: "FieldInstanceValueFloat",
    errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: () => EMPTY_ARRAY,
    getterIfTabularValue: (fieldInstanceValue) =>
      fieldInstanceValue.tabularValueFloat?.map((value) => ({
        rowNumber: value.rowNumber,
        value: value.value ?? null,
      })) ?? EMPTY_ARRAY,
  });
}
